import { apiService, ApiResponse } from './apiService'

/**
 * Field category for category-based system
 *
 * Categories define field behavior across all layers (backend, frontend, lifecycle, permissions).
 * The category determines writability, visibility, and copy-on-lifecycle behavior.
 */
export type FieldCategory =
  | 'identity'        // Primary key (uid) - not writable, not visible
  | 'tenant_context'  // Tenant scope (orgId) - not writable, not visible
  | 'audit'           // Audit fields (createdAt, modifiedAt, version) - not writable, visible
  | 'soft_delete'     // Soft delete (deletedAt, deletedBy) - not writable, not visible
  | 'computed'        // Computed fields (keyComboHash, *KeyComboHash) - not writable, not visible
  | 'lifecycle'       // Lifecycle fields (lifecycleState, effectiveFrom, etc.) - not writable, visible
  | 'business'        // Business data (default) - writable, visible, copied on lifecycle

/**
 * Field metadata from schema YAML
 *
 * These fields match what the backend FieldDefinition.to_json() returns.
 * Do NOT add fields here that don't exist in the backend.
 */
export interface FieldMetadata {
  // Basic properties
  name?: string // Field name (camelCase) - may not be in response
  db_column: string // Database column name (snake_case with rp_ prefix)
  py_type: string // Python type: 'string', 'integer', 'numeric', 'bool', 'timestamp', 'uuid', etc.
  title?: string | null // Human-readable title
  description?: string | null // Field description/help text

  // Validation properties
  required_on_create?: boolean // True if required on creation
  immutable_post_create?: boolean // Cannot be changed after creation
  nullable?: boolean // Whether field accepts NULL values (default true)
  allowed_values?: AllowedValue[] | null // Enum values if applicable (strings or {label, value} objects)
  regex?: string | null // Validation regex pattern
  min_value?: number | null // For numeric fields
  max_value?: number | null // For numeric fields
  min_length?: number | null // For string fields
  max_length?: number | null // For string fields

  // Array/collection properties
  is_array?: boolean // True if this is an array field
  element_type?: string | null // For array fields, the element type

  // Relationship properties
  is_fk?: boolean // True if this is a foreign key
  fk_target_model?: string | null // Target model for FK

  // System properties
  system?: boolean // True for system-managed fields (legacy - prefer category)
  default?: any // Default value for field
  is_visible?: boolean // Default UI table visibility (defaults to true if not specified)

  // Category-based system (preferred over individual flags)
  category?: FieldCategory // Field category determining behavior (default: 'business')
  effective_category?: FieldCategory // Auto-detected category based on field name
  effective_writable?: boolean // Derived from category - whether field can be written
  effective_visible?: boolean // Derived from category - whether field should be shown
  copy_on_lifecycle?: boolean // Derived from category - whether copied during lifecycle ops

  // Access control
  read_roles?: string[] // Roles that can read this field
  write_roles?: string[] // Roles that can write this field

  // Display ordering (general)
  order?: number // Display order for UI (tables, lists)

  // Form-specific properties
  form_section?: string | null // Section name (references _meta.form_sections)
  form_order?: number | null // Order within form section
  recommended?: boolean // Show in "common fields" quick-create view

  // Table display
  table_default?: boolean // Whether field appears in default table column set

  // Form rendering enhancements
  placeholder_text?: string | null // Input placeholder hint
  component_type?:
    | 'input'
    | 'textarea'
    | 'password'
    | 'hidden'
    | 'select'
    | 'radio'
    | 'switch'
    | 'checkbox'
    | 'datepicker'
    | 'inputnumber'
    | null // Override py_type inference
  display_format?: string | null // Locale-aware formatting (currency, number:2, percent, date, etc.)
  conditional_visibility?: {
    depends_on: string
    constraint_type:
      | 'EQUAL'
      | 'NOT_EQUAL'
      | 'IS_NULL'
      | 'IS_NOT_NULL'
      | 'GT'
      | 'GE'
      | 'LT'
      | 'LE'
      | 'IN'
      | 'NOT_IN'
    value?: unknown
    values?: unknown[]
  } | null // Show/hide based on other field value
  autocomplete?: boolean // Enable typeahead from distinct values
  date_semantics?: 'on_activation' | 'end_of_time' | 'beginning_of_time' | null // NULL behavior for date fields

  // Mode-based visibility (create/edit/view)
  visible_in_modes?: ('create' | 'edit' | 'view')[] | null // Modes where field is shown (null = all modes)

  // Editability control
  readonly?: boolean // Field is always read-only even in edit mode (for lifecycle-driven fields)
}

/**
 * Allowed value option (can be string or object with label/value)
 */
export type AllowedValue = string | { label: string; value: string }

/**
 * Form section definition from _meta.form_sections
 *
 * Sections group related fields in form UIs.
 * Defined in YAML _meta block, referenced by field.form_section.
 */
export interface FormSection {
  name: string // Section name/key (e.g., "basic_info") - used for both identification and display
  order: number // Display order (1, 2, 3...)
  description?: string | null // Optional section description
}

/**
 * Display metadata from _meta.display for UI rendering
 *
 * Provides user-facing labels, icons, and descriptions for the model.
 * Always present with defaults if not defined in YAML.
 */
export interface DisplayMetadata {
  label: string // Plural label (e.g., "Offerings") - default: "Things"
  label_singular: string // Singular label (e.g., "Offering") - default: "Thing"
  icon: string // Ant Design icon name (e.g., "ShoppingOutlined") - default: "QuestionOutlined"
  description: string // Model description - default: "Things are things we use"
  empty_message?: string // Optional custom empty state message
  title_fields?: string[] // Fields for friendly record titles (e.g., modal titles, tooltips)
}

/**
 * Default filter definition from _meta.default_filters
 *
 * Specifies filters applied on initial table load when no user
 * filters are configured. Users can reset to these defaults.
 */
export interface DefaultFilter {
  key: string // API field name (e.g., "lifecycleState")
  constraint_type: string // Operator (EQUAL, IN, IS_NULL, etc.)
  value?: unknown // Single value for most operators
  values?: unknown[] // List for IN/NOT_IN operators
  value2?: unknown // Second value for BETWEEN operator
}

/**
 * Default sort definition from _meta.default_sort
 *
 * Specifies sort order applied on initial table load when no user
 * sorting is configured. Users can reset to these defaults.
 */
export interface DefaultSort {
  key: string // API field name (e.g., "modifiedAt")
  direction: 'asc' | 'desc' // Sort direction
  nulls?: 'FIRST' | 'LAST' // NULL handling (optional)
}

/**
 * Preset filter definition
 *
 * Filter to apply when a preset option is selected.
 * If null, means "no filter on this field" (show all values).
 */
export interface PresetFilter {
  constraint_type: string // Operator (EQUAL, IN, etc.)
  value?: unknown // Single value for most operators
  values?: unknown[] // List for IN/NOT_IN operators
  value2?: unknown // Second value for BETWEEN operator
}

/**
 * Preset option definition from _meta.presets.options
 *
 * Each preset represents a quick-filter button (e.g., "Drafts", "Active", "Expired").
 * When clicked, applies the associated filter to the preset's field.
 */
export interface PresetOption {
  key: string // Unique key for this preset (e.g., "draft", "active")
  label: string // Display label (e.g., "Drafts", "Active")
  icon?: string | null // Ant Design icon name (e.g., "EditOutlined")
  filter: PresetFilter | null // Filter to apply (null = no filter on field)
}

/**
 * Presets configuration from _meta.presets
 *
 * Defines quick-filter buttons for a specific field (like lifecycleState).
 * Frontend renders these as a Segmented control or button group.
 */
export interface PresetsMetadata {
  field: string // Field name this preset controls (e.g., "lifecycleState")
  default?: string | null // Default selected preset key
  options: PresetOption[] // List of preset options
}

/**
 * Search configuration from _meta.search
 *
 * Controls simple search bar behavior and which fields to search.
 * When false, search is disabled entirely. When object, defines searchable fields.
 */
export interface SearchConfig {
  fields: string[] // Field names to search (OR logic)
  placeholder?: string // Optional placeholder text for search input
}

/**
 * API endpoint configuration flags
 *
 * Granular controls for which endpoints are enabled on a model.
 * Backend sets these in YAML _meta.api.endpoints.
 * All default to true if not specified.
 */
export interface ApiEndpointsConfig {
  /** Whether CREATE endpoint is enabled (POST /) */
  create?: boolean
  /** Whether UPDATE endpoint is enabled (PUT /{uid}) */
  update?: boolean
  /** Whether DELETE endpoint is enabled (DELETE /{uid}) */
  delete?: boolean
  /** Whether LIST endpoint is enabled (GET /) */
  list?: boolean
  /** Whether GET endpoint is enabled (GET /{uid}) */
  get?: boolean
  /** Whether SEARCH endpoint is enabled (POST /search) */
  search?: boolean
}

/**
 * API configuration from backend _meta.api
 *
 * Controls which API operations are available for this model.
 */
export interface ApiConfig {
  /** Endpoint availability flags */
  endpoints?: ApiEndpointsConfig
}

/**
 * Lifecycle action metadata
 *
 * Describes a single lifecycle action (e.g., activate, expire, modify)
 * including UI rendering hints and behavior flags.
 */
export interface LifecycleAction {
  /** Display order for slot-based rendering (lower = first). Lifecycle actions use 10-90. */
  order?: number
  /** Button label (e.g., 'Activate', 'Expire', 'Create New Version') */
  label: string
  /** Ant Design icon name (e.g., 'CheckCircleOutlined') */
  icon?: string
  /** Button type for Ant Design Button component */
  buttonType?: 'primary' | 'default' | 'dashed' | 'link' | 'text'
  /** States from which this action is valid (e.g., ['draft'] for activate) */
  fromStates: string[]
  /** Target state after action (null if creates new record, e.g., modify) */
  toState: string | null
  /** Confirmation message (null = no confirmation, opens editor instead) */
  confirmationMessage?: string | null
  /** Success message (supports {entity} placeholder) */
  successMessage?: string
  /** Render as danger/destructive button (e.g., expire) */
  danger?: boolean
  /** Whether action requires body data (e.g., modify needs changes) */
  requiresBody?: boolean
  /** Whether action opens an editor form instead of direct API call */
  opensEditor?: boolean
}

/**
 * Lifecycle metadata from backend
 *
 * Included in metadata response when service has StatefulServiceMixin
 * (MasterDataLifecycleMixin, WorkflowLifecycleMixin, etc.)
 */
export interface LifecycleMetadata {
  /** Whether lifecycle is enabled */
  enabled: boolean
  /** Field name holding the state (e.g., 'lifecycleState', 'state') */
  stateField: string
  /** Available lifecycle actions with UI metadata */
  actions: Record<string, LifecycleAction>
  /**
   * All valid lifecycle states from StateConfig.
   * This is the authoritative list of valid states (e.g., ['draft', 'pending_approval', 'active', 'expired', 'superseded']).
   * Used for filtering to exclude records with invalid/garbage state values.
   * When present, this takes precedence over deriving states from actions.
   */
  states?: string[]
}

/**
 * Record action metadata (similar to LifecycleAction)
 *
 * For record existence operations: delete, undelete, purge
 * These are auto-generated from backend based on enabled API endpoints.
 */
export interface RecordAction {
  /** Display order for slot-based rendering (lower = first). Record actions use 100+. */
  order?: number
  /** Button label (e.g., 'Delete', 'Discard', 'Restore') */
  label: string
  /** Ant Design icon name */
  icon?: string
  /** Button type */
  buttonType?: 'primary' | 'default' | 'dashed' | 'link' | 'text'
  /** Render as danger button (red styling) */
  danger?: boolean
  /** Confirmation message (null = no confirmation). Supports {entity} placeholder. */
  confirmationMessage?: string | null
  /** Success message. Supports {entity} placeholder. */
  successMessage?: string
  /** Lifecycle states where action is valid (null = all states) */
  fromStates?: string[] | null
  /** Only show for soft-deleted records */
  requiresDeleted?: boolean
  /** Can be used in bulk operations */
  bulkable?: boolean
}

/**
 * Record actions metadata from backend
 *
 * Contains delete/undelete/purge action configurations.
 * Automatically populated based on which API endpoints are enabled.
 */
export interface RecordActionsMetadata {
  /** Soft delete action */
  delete?: RecordAction
  /** Restore from soft-delete action */
  undelete?: RecordAction
  /** Permanent delete action (typically disabled by default) */
  purge?: RecordAction
}

/**
 * Comparison preset strategy type
 *
 * Valid strategies for quick comparison access.
 */
export type ComparisonStrategy =
  | 'active_now'    // Currently effective active record
  | 'active_on'     // Active record at configurable date
  | 'active_latest' // Active/expired with max effectiveFrom
  | 'active_prior'  // Active/expired with max effectiveTo < NOW
  | 'pick'          // Manual selection via selector

/**
 * Single comparison preset option (like 'vs Now', 'vs Prior')
 *
 * Defined in YAML _meta.comparison.presets.options
 */
export interface ComparisonPreset {
  /** Unique identifier (e.g., "active_now") */
  key: string
  /** Display label (e.g., "Now") */
  label: string
  /** Ant Design icon name (e.g., "CheckCircleOutlined") */
  icon?: string
  /** Selection strategy */
  strategy: ComparisonStrategy
  /** Tooltip help text */
  description?: string
}

/**
 * Presets configuration for comparison quick-access buttons
 *
 * Defined in YAML _meta.comparison.presets
 */
export interface ComparisonPresetsConfig {
  /** Default preset key to auto-select when entering comparison mode */
  default?: string
  /** Available preset options */
  options: ComparisonPreset[]
}

/**
 * Comparison configuration from backend _meta.comparison
 *
 * Enables side-by-side record comparison in the detail drawer
 * for lifecycle tables with versioned records.
 */
export interface ComparisonConfig {
  /** Whether comparison mode is enabled */
  enabled: boolean
  /** Default mode: 'same_entity' for version comparison, 'search' for any record */
  defaultMode: 'same_entity' | 'search'
  /** Allow switching to search mode */
  allowSearchMode: boolean
  /** Threshold for eager loading all versions (vs paginated) */
  maxVersionsEager: number
  /** Page size for paginated version loading */
  paginationSize: number
  /** Custom display order for lifecycle states */
  stateOrder?: string[]
  /** Fields to highlight in comparison (business-critical) */
  keyFields: string[]
  /** Fields to exclude from comparison view (internal/system) */
  excludeFields: string[]
  /** Panel title template (supports {entity} placeholder) */
  panelTitle: string
  /** Quick-access comparison presets */
  presets?: ComparisonPresetsConfig
  /** Field to use for timeline segment labels, or null to disable labels */
  timelineLabelField?: string | null
  /** Whether to prefix "NEW" to the focus segment in timeline */
  showNewPrefix?: boolean
}

/**
 * Access permissions for a model
 *
 * Indicates whether the current user can read/write this model.
 * These flags are UX hints only - the server validates all operations.
 *
 * @property canRead - True if user can view this model's data
 * @property canWrite - True if user can create/update/delete this model's data
 */
export interface AccessPermissions {
  canRead: boolean
  canWrite: boolean
}

/**
 * Metadata response structure
 *
 * @property fields - Field definitions for the model
 * @property access - Optional access permissions (UX only - server validates all operations)
 *                    May be undefined for backward compatibility with older API responses.
 *
 * @example Usage with conditional rendering
 * ```typescript
 * // Disable create button if user can't write
 * <Button
 *   disabled={!metadata?.access?.canWrite}
 *   onClick={handleCreate}
 * >
 *   Create Offering
 * </Button>
 *
 * // Hide entire section if user can't read
 * {metadata?.access?.canRead && (
 *   <OfferingsTable />
 * )}
 *
 * // Safe default for missing access field (backward compatibility)
 * const canWrite = metadata?.access?.canWrite ?? true // Default to true if undefined
 * ```
 */
export interface MetadataResponse {
  fields: Record<string, FieldMetadata>
  /**
   * Optional access permissions for this model.
   * UX only - server validates all operations.
   * May be undefined for backward compatibility.
   */
  access?: AccessPermissions
  /**
   * Optional form sections for organizing fields in forms.
   * Defined in YAML _meta.form_sections.
   * Fields reference sections via form_section field.
   */
  form_sections?: FormSection[]
  /**
   * Optional display metadata for UI rendering.
   * Defined in YAML _meta.display.
   * Always present with defaults if not defined in YAML.
   */
  display?: DisplayMetadata
  /**
   * Optional base filters that are always applied.
   * Defined in YAML _meta.base_filters.
   * These filters are permanent and cannot be removed by users.
   */
  base_filters?: DefaultFilter[]
  /**
   * Optional default filters for initial table load.
   * Defined in YAML _meta.default_filters.
   * Users can reset to these defaults.
   */
  default_filters?: DefaultFilter[]
  /**
   * Optional default sort order for initial table load.
   * Defined in YAML _meta.default_sort.
   * Users can reset to these defaults.
   */
  default_sort?: DefaultSort[]
  /**
   * Optional presets configuration for quick-filter buttons.
   * Defined in YAML _meta.presets.
   * Frontend renders as Segmented control or button group.
   */
  presets?: PresetsMetadata
  /**
   * Optional search configuration for simple search bar.
   * Defined in YAML _meta.search.
   * - When false: search is disabled entirely (hidden from UI)
   * - When SearchConfig object: defines which fields to search and optional placeholder
   * - When undefined: falls back to heuristic (search 'name' field if exists, else first text field)
   */
  search?: SearchConfig | false
  /**
   * Optional lifecycle configuration for entities with state machine.
   * Present when service inherits from StatefulServiceMixin (or subclass).
   * Provides action metadata for UI rendering (buttons, confirmations, etc.)
   */
  lifecycle?: LifecycleMetadata
  /**
   * Optional API configuration controlling endpoint availability.
   * Defined in YAML _meta.api.
   * Use to determine if create/update/delete operations are allowed.
   *
   * @example Hide "New" button for read-only tables
   * ```typescript
   * const canCreate = metadata?.api?.endpoints?.create !== false
   * ```
   */
  api?: ApiConfig
  /**
   * Optional record actions for delete/undelete/purge operations.
   * Auto-configured based on enabled API endpoints.
   * Each action includes UI metadata (label, icon, confirmation, etc.)
   *
   * @example Render delete button with metadata
   * ```typescript
   * const deleteAction = metadata?.record_actions?.delete
   * if (deleteAction) {
   *   <Button danger={deleteAction.danger}>{deleteAction.label}</Button>
   * }
   * ```
   */
  record_actions?: RecordActionsMetadata
  /**
   * Optional comparison configuration for record versioning.
   * Enables side-by-side comparison of records in the detail drawer.
   * Auto-configured for tables with keyComboHash field.
   *
   * @example Check if comparison is enabled
   * ```typescript
   * if (metadata?.comparison?.enabled) {
   *   // Show "Compare" button in drawer header
   * }
   * ```
   */
  comparison?: ComparisonConfig
}

/**
 * Model schema response
 */
export interface ModelSchema {
  version: number
  fields: Record<string, FieldMetadata>
}

/**
 * API Entity from /metadata/entities endpoint
 *
 * Represents an entity with API endpoints (discovered from YAML schemas).
 */
export interface ApiEntity {
  /** Display name (e.g., "Offerings", "Audit Logs") */
  name: string
  /** API path (e.g., "/offerings", "/audit_logs") */
  path: string
  /** Optional Ant Design icon name */
  icon?: string
  /** Optional description */
  description?: string
  /** True if this is a view schema - use /metadata-read endpoint instead of /metadata */
  isView?: boolean
}

/**
 * Metadata Service
 * Provides access to schema metadata for forms and validation
 */
export class MetadataService {
  private readonly METADATA_ENDPOINT = '/metadata'

  /**
   * List all available API entities
   *
   * This is the primary method for discovering available API entities.
   * Use the returned entity.path to fetch full metadata from /{entity.path}/metadata.
   *
   * @param contains - Optional substring filter on name or path
   * @returns List of API entities with name, path, icon, and description
   *
   * @example
   * ```typescript
   * const response = await metadataService.listEntities()
   * // Returns: { entities: [{ name: "Offerings", path: "/offerings" }, ...] }
   *
   * // Fetch full metadata for an entity
   * const metadata = await metadataService.getMetadataByEndpoint(entity.path)
   * ```
   */
  async listEntities(contains?: string): Promise<ApiResponse<ApiEntity[]>> {
    const params = contains ? `?contains=${encodeURIComponent(contains)}` : ''
    const response = await apiService.get<{ entities: ApiEntity[] }>(
      `${this.METADATA_ENDPOINT}/entities${params}`,
      { suppressErrors: true }
    )

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.entities,
      } as unknown as ApiResponse<ApiEntity[]>
    }

    return response as unknown as ApiResponse<ApiEntity[]>
  }

  /**
   * @deprecated Use listEntities() instead. This endpoint has been removed.
   */
  async listModels(contains?: string): Promise<ApiResponse<string[]>> {
    console.warn('metadataService.listModels() is deprecated. Use listEntities() instead.')
    const params = contains ? `?contains=${encodeURIComponent(contains)}` : ''
    const response = await apiService.get<{ models: string[] }>(
      `${this.METADATA_ENDPOINT}/models${params}`
    )

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.models,
      } as unknown as ApiResponse<string[]>
    }

    return response as unknown as ApiResponse<string[]>
  }

  /**
   * Get full schema for a model
   *
   * @param model - Model name (e.g., 'offering', 'customer')
   * @param onlyKeys - If true, returns only field names
   * @returns Full schema with field definitions
   */
  async getModelSchema(
    model: string,
    onlyKeys?: boolean
  ): Promise<ApiResponse<ModelSchema>> {
    const params = onlyKeys ? '?only=keys' : ''
    return await apiService.get<ModelSchema>(`${this.METADATA_ENDPOINT}/${model}${params}`)
  }

  /**
   * Search fields with filtering
   *
   * Uses the per-entity CRUD router metadata endpoint: /{entity}/metadata/fields
   *
   * Supports query parameters:
   * - py_type=str - Exact match on Python type
   * - system=false - Exclude system fields
   * - title__contains=Date - Substring match on title
   * - etc.
   *
   * @param model - Model name (singular, e.g., 'offering') or endpoint path (e.g., '/offerings')
   * @param filters - Query parameters for filtering
   * @returns Filtered field metadata
   */
  async searchFields(
    model: string,
    filters?: Record<string, string | boolean>
  ): Promise<ApiResponse<MetadataResponse>> {
    const params = new URLSearchParams()
    const fieldFilters: Record<string, string | boolean> = {}

    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (key === 'system') {
          // CRUD router uses 'allow_system' (default: false = system fields excluded)
          if (value === true || value === 'true') {
            params.append('allow_system', 'true')
          }
          // system=false is the default, no param needed
        } else {
          // Other filters go into the JSON 'filters' query param
          fieldFilters[key] = value
        }
      }
    }

    if (Object.keys(fieldFilters).length > 0) {
      params.append('filters', JSON.stringify(fieldFilters))
    }

    const queryString = params.toString()
    const endpoint = this.modelToEndpoint(model)
    const url = `${endpoint}/metadata/fields${queryString ? `?${queryString}` : ''}`

    return await apiService.get<MetadataResponse>(url)
  }

  /**
   * Get fields filtered by Python type
   *
   * @param model - Model name
   * @param pyType - Python type to filter by (str, int, float, bool, date, datetime, etc.)
   * @param excludeSystem - Exclude system-managed fields (default: true)
   * @returns Fields matching the type with access permissions
   */
  async getFieldsByType(
    model: string,
    pyType: string,
    excludeSystem: boolean = true
  ): Promise<ApiResponse<MetadataResponse>> {
    const filters: Record<string, string | boolean> = {
      py_type: pyType,
    }

    if (excludeSystem) {
      filters.system = false
    }

    return await this.searchFields(model, filters)
  }

  /**
   * Get all non-system fields for a model
   *
   * @param model - Model name
   * @returns All non-system fields with access permissions
   */
  async getNonSystemFields(
    model: string
  ): Promise<ApiResponse<MetadataResponse>> {
    return await this.searchFields(model, { system: false })
  }

  /**
   * Get only visible fields for a model (for default table rendering)
   *
   * Filters out fields where is_visible is explicitly set to false.
   * Fields without is_visible defined default to visible (true).
   *
   * @param model - Model name (e.g., 'offering', 'customer')
   * @returns Visible field metadata with access permissions
   */
  async getVisibleFields(
    model: string
  ): Promise<ApiResponse<MetadataResponse>> {
    const response = await this.getNonSystemFields(model)

    if (!response.success || !response.data) {
      return response
    }

    // Filter by is_visible on client side
    const visibleFields: Record<string, FieldMetadata> = {}
    Object.entries(response.data.fields).forEach(([key, field]) => {
      if (field.is_visible !== false) {
        // Default to true if not specified
        visibleFields[key] = field
      }
    })

    return {
      success: true,
      data: {
        fields: visibleFields,
        access: response.data.access,
      },
      message: `Found ${Object.keys(visibleFields).length} visible fields for ${model}`,
    }
  }

  /**
   * Get fields suitable for default table display
   * (visible, non-system fields)
   *
   * This is a convenience method that combines system filtering
   * and visibility filtering for the most common UI use case.
   *
   * @param model - Model name
   * @returns Fields for table display with access permissions
   */
  async getTableDisplayFields(
    model: string
  ): Promise<ApiResponse<MetadataResponse>> {
    // getVisibleFields already calls getNonSystemFields internally,
    // so this effectively gets non-system AND visible fields
    return await this.getVisibleFields(model)
  }

  /**
   * Map UI data type to Python type names
   *
   * Used to filter fields by user-friendly data type names
   *
   * @param uiType - UI data type (Text, Number, Date, Boolean)
   * @returns Array of Python type names that match (lowercase)
   */
  getPythonTypesForUIType(uiType: string): string[] {
    const typeMap: Record<string, string[]> = {
      Text: ['string', 'str', 'uuid'],  // YAML uses 'string', not 'str'; uuid also maps to text
      Number: ['integer', 'int', 'float', 'numeric', 'Decimal'],  // YAML uses 'integer' and 'numeric'
      Date: ['date', 'datetime', 'timestamp', 'timestamptz'],  // YAML uses 'timestamp' and 'timestamptz'
      Boolean: ['bool'],
    }

    return typeMap[uiType] || []
  }

  /**
   * Get fields for dropdown filtered by UI data type
   *
   * @param model - Model name
   * @param uiType - UI data type (Text, Number, Date, Boolean)
   * @returns Fields matching the UI data type with access permissions
   */
  async getFieldsForUIType(
    model: string,
    uiType: string
  ): Promise<ApiResponse<MetadataResponse>> {
    const pyTypes = this.getPythonTypesForUIType(uiType)

    if (pyTypes.length === 0) {
      return {
        success: false,
        message: `Unknown UI type: ${uiType}`,
        errors: [{ code: 'INVALID_TYPE', message: `UI type '${uiType}' is not recognized` }],
      }
    }

    // Get all non-system fields
    const response = await this.getNonSystemFields(model)

    if (!response.success || !response.data) {
      return response
    }

    // Filter by Python types on client side (backend doesn't support OR queries)
    const filteredFields: Record<string, FieldMetadata> = {}
    Object.entries(response.data.fields).forEach(([key, field]) => {
      if (pyTypes.includes(field.py_type)) {
        filteredFields[key] = field
      }
    })

    return {
      success: true,
      data: {
        fields: filteredFields,
        access: response.data.access,
      },
      message: `Found ${Object.keys(filteredFields).length} fields matching type ${uiType}`,
    }
  }

  /**
   * Get metadata for an API endpoint
   *
   * This method is designed for the self-contained MetadataTable component.
   * It fetches metadata from the endpoint's metadata sub-path.
   *
   * @param endpoint - API endpoint path (e.g., '/offerings', '/audit_logs')
   * @param useViewMetadata - If true, fetches from /metadata-read instead of /metadata
   * @returns Full metadata response with fields and access permissions
   *
   * @example
   * ```typescript
   * // Fetch write model metadata
   * const response = await metadataService.getMetadataByEndpoint('/offerings')
   *
   * // Fetch view model metadata (enriched fields)
   * const response = await metadataService.getMetadataByEndpoint('/audit_logs', true)
   * ```
   */
  async getMetadataByEndpoint(
    endpoint: string,
    useViewMetadata: boolean = false,
    options?: { suppressErrors?: boolean }
  ): Promise<ApiResponse<MetadataResponse>> {
    // Normalize endpoint: ensure it starts with / and remove trailing /
    const normalizedEndpoint = endpoint.startsWith('/')
      ? endpoint.replace(/\/$/, '')
      : `/${endpoint}`.replace(/\/$/, '')

    // Build metadata path based on view flag
    const metadataPath = useViewMetadata
      ? `${normalizedEndpoint}/metadata-read`
      : `${normalizedEndpoint}/metadata`

    // Fetch metadata with system fields excluded
    // Note: Backend uses 'allow_system' not 'system' query param
    const response = await apiService.get<MetadataResponse>(
      `${metadataPath}?allow_system=false`,
      { suppressErrors: options?.suppressErrors }
    )

    if (!response.success || !response.data) {
      return response
    }

    // Filter out is_visible: false fields on client side
    const visibleFields: Record<string, FieldMetadata> = {}
    Object.entries(response.data.fields).forEach(([key, field]) => {
      if (field.is_visible !== false) {
        visibleFields[key] = field
      }
    })

    return {
      success: true,
      data: {
        ...response.data,
        fields: visibleFields,
      },
      message: `Loaded metadata for ${normalizedEndpoint}`,
    }
  }

  /**
   * Extract model name from endpoint path
   *
   * Converts plural endpoint to singular model name for legacy compatibility.
   *
   * @param endpoint - API endpoint path (e.g., '/offerings', '/audit_logs')
   * @returns Singular model name (e.g., 'offering', 'audit_log')
   */
  /**
   * Convert a singular model name to its API endpoint path.
   * Reverse of extractModelFromEndpoint.
   *
   * If the input already looks like an endpoint (starts with '/'), returns as-is.
   *
   * @param model - Singular model name (e.g., 'offering', 'currency')
   * @returns Endpoint path (e.g., '/offerings', '/currencies')
   */
  modelToEndpoint(model: string): string {
    // Already an endpoint path
    if (model.startsWith('/')) {
      return model.replace(/\/+$/, '')
    }

    // Standard pluralization (reverse of extractModelFromEndpoint)
    if (model.endsWith('y') && !/[aeiou]y$/i.test(model)) {
      return '/' + model.slice(0, -1) + 'ies'
    }
    return '/' + model + 's'
  }

  extractModelFromEndpoint(endpoint: string): string {
    // Remove leading/trailing slashes and get the table name
    const tableName = endpoint.replace(/^\/+|\/+$/g, '')

    // Simple singularization: remove trailing 's' or 'es'
    // This covers most cases: offerings->offering, customers->customer
    if (tableName.endsWith('ies')) {
      return tableName.slice(0, -3) + 'y' // currencies -> currency
    } else if (tableName.endsWith('es') && !tableName.endsWith('ses')) {
      return tableName.slice(0, -2) // statuses handled separately
    } else if (tableName.endsWith('s')) {
      return tableName.slice(0, -1) // offerings -> offering
    }

    return tableName
  }
}

// Export singleton instance
export const metadataService = new MetadataService()
export default metadataService

// =============================================================================
// Category-based field behavior utilities
// =============================================================================

/**
 * Category behavior definitions
 *
 * Each category defines whether fields are writable, visible, and copied on lifecycle.
 */
export const CATEGORY_BEHAVIORS: Record<FieldCategory, { writable: boolean; visible: boolean; copyOnLifecycle: boolean }> = {
  identity: { writable: false, visible: false, copyOnLifecycle: false },
  tenant_context: { writable: false, visible: false, copyOnLifecycle: false },
  audit: { writable: false, visible: true, copyOnLifecycle: false },
  soft_delete: { writable: false, visible: false, copyOnLifecycle: false },
  computed: { writable: false, visible: false, copyOnLifecycle: false },
  lifecycle: { writable: false, visible: true, copyOnLifecycle: false },
  business: { writable: true, visible: true, copyOnLifecycle: true },
}

/**
 * Check if a field is editable based on its metadata
 *
 * Uses category-based system if available, falls back to legacy flags.
 * Priority: effective_writable > category behavior > legacy system flag > readonly flag
 *
 * @param field - Field metadata
 * @returns True if field can be edited by users
 */
export function isFieldEditable(field: FieldMetadata): boolean {
  // Use effective_writable if backend provides it
  if (field.effective_writable !== undefined) {
    return field.effective_writable
  }

  // Use category behavior if category is specified
  if (field.category || field.effective_category) {
    const category = (field.category || field.effective_category) as FieldCategory
    return CATEGORY_BEHAVIORS[category]?.writable ?? true
  }

  // Fall back to legacy flags
  if (field.system) {
    return false
  }
  if (field.readonly) {
    return false
  }

  return true
}

/**
 * Check if a field should be shown in the UI based on its metadata
 *
 * Uses category-based system if available, falls back to legacy is_visible flag.
 *
 * @param field - Field metadata
 * @returns True if field should be displayed to users
 */
export function isFieldVisible(field: FieldMetadata): boolean {
  // Use effective_visible if backend provides it
  if (field.effective_visible !== undefined) {
    return field.effective_visible
  }

  // Use category behavior if category is specified
  if (field.category || field.effective_category) {
    const category = (field.category || field.effective_category) as FieldCategory
    return CATEGORY_BEHAVIORS[category]?.visible ?? true
  }

  // Fall back to legacy is_visible flag (defaults to true if not specified)
  return field.is_visible !== false
}

/**
 * Check if a field should be shown in forms based on its category and visibility
 *
 * Combines category visibility with form-specific logic.
 *
 * @param field - Field metadata
 * @returns True if field should appear in forms
 */
export function showInForm(field: FieldMetadata): boolean {
  return isFieldVisible(field)
}

/**
 * Check if a field should be shown in tables based on its category and visibility
 *
 * Combines category visibility with table-specific logic.
 *
 * @param field - Field metadata
 * @returns True if field should appear in table columns
 */
export function showInTable(field: FieldMetadata): boolean {
  return isFieldVisible(field)
}

/**
 * Check if a field should be included in record comparison views
 *
 * Excludes system fields and fields marked as not visible.
 *
 * @param field - Field metadata
 * @returns True if field should appear in comparison views
 */
export function showInComparison(field: FieldMetadata): boolean {
  // Use category-based logic if available
  if (field.category || field.effective_category) {
    const category = (field.category || field.effective_category) as FieldCategory
    // Exclude computed and identity fields from comparison
    if (category === 'identity' || category === 'computed' || category === 'tenant_context') {
      return false
    }
  }

  // Include if visible
  return isFieldVisible(field)
}
